import Konva from "konva";
import { useAtomValue, useSetAtom } from "jotai";
import { Group } from "react-konva";
import { hoveredTemplatePreviewAtom, posScaleAtom } from "state-atoms/stage-atoms";
import { userAtom } from "state-atoms";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { centerElementsAroundOrigin, separateRawDataToElementsAndMetadata } from "frontend/utils/template-utils";
import { getElementTypeForId } from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import ElementComponent from "elements/base/konva-component";
import { getElementProvider } from "elements/index";
import { CanvasElement } from "frontend/canvas-designer-new/canvas-element";
import { calculateTemplateBoundaries, calculateViewportCenter, useMockBoardContext } from "./utils";
import { noop } from "utils/fn-utils";

export default function TemplateGhostPreview() {
  const hoveredTemplate = useAtomValue(hoveredTemplatePreviewAtom);
  const setHoveredTemplate = useSetAtom(hoveredTemplatePreviewAtom);
  const posScale = useAtomValue(posScaleAtom);
  const user = useAtomValue(userAtom);
  const groupRef = useRef<Konva.Group>(null);

  const [dynamicScale, setDynamicScale] = useState(1);
  const [groupOffset, setGroupOffset] = useState({ x: 0, y: 0 });
  const [scaledTemplateId, setScaledTemplateId] = useState<string | null>(null);

  const { cleanedElements, elementsWithPrefix, viewportCenter } = useMemo(() => {
    if (!hoveredTemplate) {
      return { cleanedElements: [], elementsWithPrefix: [], viewportCenter: { x: 0, y: 0 } };
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const boundaries = calculateTemplateBoundaries(hoveredTemplate.boundaries, viewportHeight);
    const viewportCenter = calculateViewportCenter(boundaries, viewportWidth, posScale);

    const { elements } = separateRawDataToElementsAndMetadata(hoveredTemplate.templateData);
    const centeredData = centerElementsAroundOrigin(elements);
    const cleanedElements = Object.entries(centeredData);

    const elementsWithPrefix = cleanedElements.map(([id, data]) => [`cElement-${id}`, data] as [string, unknown]);

    return { cleanedElements, elementsWithPrefix, viewportCenter };
  }, [hoveredTemplate, posScale]);

  const isLoggedIn = !(!user || user.isGuest);
  const boardContext = useMockBoardContext(elementsWithPrefix, isLoggedIn);

  // Reset scale when template changes
  useEffect(() => {
    setScaledTemplateId(null);
    setDynamicScale(1);
    setGroupOffset({ x: 0, y: 0 });
  }, [hoveredTemplate?.templateId]);

  // Calculate dynamic scale after render by measuring the actual Konva layer
  useEffect(() => {
    if (!groupRef.current || !hoveredTemplate) {
      setDynamicScale(1);
      setScaledTemplateId(null);
      setGroupOffset({ x: 0, y: 0 });
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      if (!groupRef.current || !hoveredTemplate) return;

      const localRect = groupRef.current.getClientRect({ skipTransform: true });

      const screenRect = groupRef.current.getClientRect();
      const templateWidth = screenRect.width;
      const templateHeight = screenRect.height;

      // Guard against empty templates (width or height is 0)
      if (
        templateWidth === 0 ||
        templateHeight === 0 ||
        !Number.isFinite(templateWidth) ||
        !Number.isFinite(templateHeight)
      ) {
        setDynamicScale(1);
        setGroupOffset({ x: 0, y: 0 });
        setScaledTemplateId(hoveredTemplate.templateId);
        return;
      }

      const offsetX = localRect.x + localRect.width / 2;
      const offsetY = localRect.y + localRect.height / 2;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const boundaries = calculateTemplateBoundaries(hoveredTemplate.boundaries, viewportHeight);

      const availableHeightPixels = boundaries.availableHeight;
      const availableWidthPixels = viewportWidth - 100;

      const scaleToFitHeight = (availableHeightPixels * 0.85) / templateHeight;
      const scaleToFitWidth = (availableWidthPixels * 0.85) / templateWidth;

      const calculatedScale = Math.max(0, Math.min(scaleToFitHeight, scaleToFitWidth));
      if (!Number.isFinite(calculatedScale)) {
        setDynamicScale(1);
        setGroupOffset({ x: 0, y: 0 });
        setScaledTemplateId(hoveredTemplate.templateId);
        return;
      }

      setDynamicScale(calculatedScale);
      setGroupOffset({ x: offsetX, y: offsetY });
      setScaledTemplateId(hoveredTemplate.templateId);

      const currentTransform = hoveredTemplate.ghostTransform;
      const hasChanged =
        !currentTransform ||
        currentTransform.position.x !== viewportCenter.x ||
        currentTransform.position.y !== viewportCenter.y ||
        currentTransform.offset.x !== offsetX ||
        currentTransform.offset.y !== offsetY ||
        currentTransform.scale !== calculatedScale;

      if (hasChanged) {
        setHoveredTemplate({
          ...hoveredTemplate,
          ghostTransform: {
            position: viewportCenter,
            offset: { x: offsetX, y: offsetY },
            scale: calculatedScale,
          },
        });
      }
    });

    return () => cancelAnimationFrame(animationFrameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredTemplate?.templateId, posScale.x, posScale.y, posScale.scale]);

  if (!hoveredTemplate || cleanedElements.length === 0) {
    return null;
  }

  return (
    <Group
      ref={groupRef}
      x={viewportCenter.x}
      y={viewportCenter.y}
      offsetX={groupOffset.x}
      offsetY={groupOffset.y}
      scaleX={dynamicScale}
      scaleY={dynamicScale}
      opacity={0.4}
      visible={scaledTemplateId === hoveredTemplate?.templateId}
      listening={false}
    >
      {cleanedElements.map(([elementId, elementData]) => {
        const elementType = getElementTypeForId(elementId);
        const provider = getElementProvider(elementType);
        const data = elementData as Record<string, unknown>;

        if (
          (elementType === "taskCard" || elementType === "task" || elementType === "integrationItem") &&
          data.containerId
        ) {
          return null;
        }

        if (provider?.isFFEnabled()) {
          return (
            <ElementComponent
              key={elementId}
              id={elementId}
              type={elementType}
              context={boardContext}
              elementData={elementData as never}
              allElementsData={cleanedElements}
              onResize={noop}
            />
          );
        }

        return (
          <CanvasElement
            key={elementId}
            uniqueId={elementId}
            elementData={elementData}
            allElementsData={cleanedElements}
            documentId={undefined}
            onResize={noop}
            onChangeElement={noop}
            isSelected={false}
            drawOutlineAroundElements={false}
            isEditing={false}
            isEditingLink={false}
            isSelectable={false}
            isFrameHighlighted={false}
            patchCanvasElement={noop}
            patchAnything={noop}
            onElementsMutationEnded={noop}
            isReadOnly={true}
          />
        );
      })}
    </Group>
  );
}
